import React from "react";

type PaymentStatusLoadingProps = {
  title?: string;
  message?: string;
};

const COIN_SIZE = 55;
// ✅ shorter bar
const BAR_WIDTH = "min(360px, 82vw)";

const PaymentStatusLoading: React.FC<PaymentStatusLoadingProps> = ({
  title = "Confirming Payment",
  message = "Please wait while we are confirming your payment status.",
}) => {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.loaderWrap} aria-hidden="true">
          {/* Coins */}
          <div style={styles.coinsLayer}>
            {[0, 500, 1000].map((delay, index) => (
              <div
                key={index}
                style={{ ...styles.coinWrap, animationDelay: `${delay}ms` }}
              >
                <div style={styles.coin}>
                  <div style={styles.coinInner}>
                    <span style={styles.rupee}>₹</span>
                    <div style={styles.sparkle} />
                  </div>
                </div>
                <div style={styles.coinShadow} />
              </div>
            ))}
          </div>

          {/* Track */}
          <div style={styles.track} />
        </div>

        <h1 style={styles.title}>{title}</h1>
        <p style={styles.message}>{message}</p>
      </div>

      <style>{cssKeyframes}</style>
    </div>
  );
};

export default PaymentStatusLoading;

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#ffffff",
    padding: 24,
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    color: "#0F172A",
  },

  container: {
    width: "min(560px, 92vw)",
    textAlign: "center",
  },

  loaderWrap: {
    width: BAR_WIDTH, // ✅ shorter width
    margin: "0 auto 22px",
    position: "relative",
    paddingTop: 52, // room for coins
  },

  coinsLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: COIN_SIZE + 14,
    pointerEvents: "none",
  },

  // Wrapper travels left->right + fades
  coinWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    width: COIN_SIZE,
    height: COIN_SIZE + 14,
    animation: "coinTravel 1.9s linear infinite",
    willChange: "transform, opacity",
  },

  // Coin body (now clearly rolling: rotate + small bounce)
  coin: {
    width: COIN_SIZE,
    height: COIN_SIZE,
    borderRadius: 999,
    background:
      "linear-gradient(180deg,#FFE07A 0%,#FFC83A 40%,#F0A700 100%)",
    border: "3px solid #F4C533",
    boxShadow:
      "0 10px 16px rgba(0,0,0,0.14), inset 0 4px 6px rgba(255,255,255,0.55)",
    display: "grid",
    placeItems: "center",
    position: "relative",
    animation: "coinRoll 520ms linear infinite",
  },

  coinInner: {
    width: "78%",
    height: "78%",
    borderRadius: 999,
    background:
      "radial-gradient(circle at 30% 30%,#FFEFA6 0%,#FFC53A 55%,#F2A700 100%)",
    display: "grid",
    placeItems: "center",
    position: "relative",
    boxShadow: "inset 0 0 0 2px rgba(150,85,0,0.18)",
  },

  rupee: {
    fontWeight: 900,
    fontSize: 20,
    color: "#B87400",
    textShadow: "0 1px 0 rgba(255,255,255,0.35)",
    transform: "translateY(-1px)",
  },

  sparkle: {
    position: "absolute",
    width: 14,
    height: 14,
    top: 5,
    right: 5,
    borderRadius: 999,
    background:
      "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 70%)",
    animation: "sparkle 1.1s ease-in-out infinite",
    pointerEvents: "none",
    mixBlendMode: "screen",
  },

  // Shadow under the coin (helps sell “rolling”)
  coinShadow: {
    position: "absolute",
    left: "50%",
    bottom: 0,
    width: COIN_SIZE - 6,
    height: 8,
    transform: "translateX(-50%)",
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.16)",
    filter: "blur(4px)",
    animation: "shadowPulse 520ms linear infinite",
    opacity: 0.35,
  },

  track: {
    height: 14,
    borderRadius: 999,
    background: "#EEF1F6",
    border: "1px solid #E2E8F0",
    boxShadow: "inset 0 1px 0 rgba(15,23,42,0.05)",
  },

  title: {
    fontSize: 20,
    fontWeight: 700,
    margin: "10px 0 8px",
    letterSpacing: "-0.01em",
  },

  message: {
    fontSize: 14,
    color: "rgba(15,23,42,0.7)",
    margin: 0,
    lineHeight: 1.55,
  },
};

const cssKeyframes = `
@keyframes coinTravel {
  0%   { transform: translateX(0px); opacity: 0; }
  10%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateX(calc(${BAR_WIDTH} - ${COIN_SIZE}px)); opacity: 0; }
}

/* True visible rolling + slight bounce */
@keyframes coinRoll {
  0%   { transform: translateY(0px) rotate(0deg); }
  25%  { transform: translateY(1px) rotate(90deg); }
  50%  { transform: translateY(0px) rotate(180deg); }
  75%  { transform: translateY(1px) rotate(270deg); }
  100% { transform: translateY(0px) rotate(360deg); }
}

/* Shadow changes to match bounce */
@keyframes shadowPulse {
  0%   { transform: translateX(-50%) scaleX(1.0); opacity: 0.30; }
  25%  { transform: translateX(-50%) scaleX(0.85); opacity: 0.22; }
  50%  { transform: translateX(-50%) scaleX(1.0); opacity: 0.30; }
  75%  { transform: translateX(-50%) scaleX(0.85); opacity: 0.22; }
  100% { transform: translateX(-50%) scaleX(1.0); opacity: 0.30; }
}

@keyframes sparkle {
  0%   { transform: scale(0.6); opacity: 0.45; }
  50%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0.6); opacity: 0.45; }
}
`;